import React, { useState } from "react";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Wand2, Loader2, X, Check } from "lucide-react";
import { TICKET_TYPES } from "../../lib/utils";
import * as api from "../../lib/api";
import { toast } from "sonner";

const typeLabel = (typeId) => TICKET_TYPES.find((t) => t.id === typeId)?.label || typeId;

function ResultField({ label, checked, onCheck, testId, children }) {
    return (
        <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:border-purple-200 cursor-pointer transition-colors">
            <input type="checkbox" checked={checked} onChange={(e) => onCheck(e.target.checked)} className="mt-0.5 accent-purple-600" data-testid={testId} />
            <div className="flex-1 min-w-0">
                <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{label}</span>
                {children}
            </div>
        </label>
    );
}

// Modal "Rédiger avec l'IA" : génère titre / type / description depuis des mots-clés.
// onApply reçoit uniquement les champs cochés : { title?, type?, description? }.
export function AiDescriptionDialog({ open, onClose, ticketType, currentTitle, onApply }) {
    const [loading, setLoading] = useState(false);
    const [keywords, setKeywords] = useState("");
    const [result, setResult] = useState(null);
    const [checks, setChecks] = useState({ title: true, type: true, description: true });

    if (!open) return null;

    const generate = async () => {
        if (!keywords.trim()) { toast.error("Tapez d'abord quelques mots-clés"); return; }
        setLoading(true);
        setResult(null);
        try {
            const r = await api.reformulateDescription({ description: keywords.trim(), ticket_type: ticketType || "", title: currentTitle || "" });
            const type = r.type || ticketType || "other";
            setResult({ title: r.title || "", type, description: r.description || "" });
            setChecks({ title: true, type: type !== ticketType, description: true });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de la génération");
        } finally { setLoading(false); }
    };

    const apply = () => {
        if (!result) return;
        const selected = {};
        if (checks.title && result.title) selected.title = result.title;
        if (checks.type && result.type && result.type !== ticketType) selected.type = result.type;
        if (checks.description && result.description) selected.description = result.description;
        onApply(selected);
        const labels = { title: "titre", type: "type", description: "description" };
        const applied = Object.keys(selected).map((k) => labels[k]);
        toast.success(applied.length > 0 ? `Champs appliqués : ${applied.join(", ")}` : "Aucun champ sélectionné");
        setKeywords("");
        setResult(null);
        onClose();
    };

    const setCheck = (k) => (v) => setChecks((p) => ({ ...p, [k]: v }));

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="ai-box-overlay">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-[95vw] overflow-hidden flex flex-col" data-testid="ai-box">
                <div className="px-5 py-4 border-b bg-purple-50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Wand2 className="w-5 h-5 text-purple-600" />
                        <h3 className="text-sm font-semibold text-purple-900">Rédiger avec l'IA</h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                </div>

                <div className="p-5 space-y-4">
                    <div>
                        <Label className="text-xs font-semibold text-gray-600 mb-1.5 block">Décrivez rapidement le problème (mots-clés, notes, phrases courtes)</Label>
                        <Textarea value={keywords} onChange={(e) => setKeywords(e.target.value)} rows={3} className="text-sm" autoFocus
                            placeholder="Ex: écran clignote, dell latitude, depuis ce matin, bureau 204, impossible travailler..." data-testid="ai-keywords-input" />
                    </div>
                    <Button type="button" onClick={generate} disabled={loading || !keywords.trim()} className="gap-2 bg-purple-600 hover:bg-purple-700 text-white" data-testid="ai-generate-submit">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                        {loading ? "Génération en cours..." : "Générer la description"}
                    </Button>

                    {result && (
                        <div className="space-y-3" data-testid="ai-result-section">
                            <Label className="text-xs font-semibold text-purple-600 block">Résultat généré — cochez les champs à appliquer</Label>
                            <ResultField label="Titre" checked={checks.title} onCheck={setCheck("title")} testId="ai-check-title">
                                <p className="text-sm text-gray-800 mt-0.5 break-words">{result.title}</p>
                            </ResultField>
                            <ResultField label="Type" checked={checks.type} onCheck={setCheck("type")} testId="ai-check-type">
                                <p className="text-sm text-gray-800 mt-0.5">
                                    {typeLabel(result.type)}
                                    {result.type === ticketType && <span className="ml-2 text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded font-medium">identique au type actuel</span>}
                                </p>
                            </ResultField>
                            <ResultField label="Description" checked={checks.description} onCheck={setCheck("description")} testId="ai-check-description">
                                <Textarea value={result.description} onChange={(e) => setResult((p) => ({ ...p, description: e.target.value }))} rows={5}
                                    className="text-sm border-purple-200 focus:ring-purple-300 mt-1" data-testid="ai-result-description" onClick={(e) => e.stopPropagation()} />
                                <p className="text-[11px] text-gray-400 mt-1">Modifiable avant application. Le texte sera ajouté à la suite de la description existante.</p>
                            </ResultField>
                        </div>
                    )}
                </div>

                <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-end gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={onClose} data-testid="ai-box-cancel">Annuler</Button>
                    {result && (
                        <Button type="button" size="sm" onClick={apply} className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white" data-testid="ai-box-apply">
                            <Check className="w-3.5 h-3.5" />Appliquer les champs sélectionnés
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
